import { useState, useEffect } from 'react';
import { DropDownSelector, TextareaDynamicPlaceholder } from '@lk-gtcom/ecomlab-components';
import "./DecisionSelector.scss"

const DecisionSelector = ({
    state,
    setState,
    currentRule,
    placeholder = "Выберите решение",
    required = false,
    mpId = null,
    onDecisionChange = null
}) => {
    const [decisions, setDecisions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDecisionData, setSelectedDecisionData] = useState(null);

    // Инициализация состояний из currentRule
    const [comment, setComment] = useState(currentRule?.decision_template?.comment || '');
    const [reason, setReason] = useState(currentRule?.decision_template?.reason || '');
    const [selectedReason, setSelectedReason] = useState(() => {
        if (currentRule?.decision_template?.reason) {
            return {
                label: currentRule.decision_template.reason,
                value: currentRule.decision_template.reason
            };
        }
        return null;
    });
    const [compensation, setCompensation] = useState(currentRule?.decision_template?.compensation || '');

    // Парсинг данных решений
    const parseDecisions = (data) => {
        return data.map(decision => ({
            ...decision,
            label: decision.code_label,
            value: decision.code,
            comment_params: decision.comment_params,
            reason_params: decision.reason_params,
            compensation_params: decision.compensation_params
        }));
    };

    // Получение решений с сервера
    const fetchDecisions = async (abortController = new AbortController()) => {
        if (!mpId) return;

        setLoading(true);
        try {
            const url = `https://apps2.ecomru.ru:4655/v1/return-rules/decisions?mp_id=${mpId}`;

            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                },
                signal: abortController.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const json = await response.json();
            const parsedDecisions = parseDecisions(json);
            setDecisions(parsedDecisions);

            // Если есть currentRule, устанавливаем выбранное решение
            if (currentRule?.decision_template) {
                const matchedDecision = parsedDecisions.find(
                    d => d.code === currentRule.decision_template.code
                );
                if (matchedDecision) {
                    // Сохраняем полные данные решения с параметрами из API
                    setSelectedDecisionData({
                        ...matchedDecision,
                        comment: currentRule.decision_template.comment || '',
                        reason: currentRule.decision_template.reason || '',
                        compensation: currentRule.decision_template.compensation || ''
                    });
                    
                    setState({
                        value: matchedDecision.code,
                        label: matchedDecision.code_label,
                        code_label: matchedDecision.code_label
                    });
                    
                    // Устанавливаем комментарий из currentRule
                    setComment(currentRule.decision_template.comment || '');
                    
                    // Проверяем, есть ли reason в доступных вариантах
                    if (currentRule.decision_template.reason && matchedDecision.reason_params?.available_reasons) {
                        const matchedReason = matchedDecision.reason_params.available_reasons.find(
                            r => r.value === currentRule.decision_template.reason || 
                                 r.label === currentRule.decision_template.reason
                        );
                        
                        if (matchedReason) {
                            setSelectedReason({
                                label: matchedReason.label,
                                value: matchedReason.value
                            });
                            setReason(matchedReason.value);
                        } else {
                            // Если не нашли, сбрасываем
                            setSelectedReason(null);
                            setReason('');
                        }
                    }
                    
                    // Устанавливаем компенсацию из currentRule
                    setCompensation(currentRule.decision_template.compensation || '');
                    
                    // Если есть колбэк, передаем данные
                    if (onDecisionChange) {
                        onDecisionChange({
                            decision: {
                                value: matchedDecision.code,
                                label: matchedDecision.code_label
                            },
                            fullData: matchedDecision,
                            comment: currentRule.decision_template.comment || '',
                            reason: currentRule.decision_template.reason || '',
                            selectedReason: selectedReason,
                            compensation: currentRule.decision_template.compensation || ''
                        });
                    }
                } else {
                    // Если решение не найдено в списке, создаем базовый объект
                    setSelectedDecisionData({
                        code: currentRule.decision_template.code,
                        code_label: currentRule.decision_template.code_label,
                        comment_params: null,
                        reason_params: null,
                        compensation_params: null,
                        comment: currentRule.decision_template.comment || '',
                        reason: currentRule.decision_template.reason || '',
                        compensation: currentRule.decision_template.compensation || ''
                    });
                    
                    // Устанавливаем комментарий из currentRule даже если решение не найдено
                    setComment(currentRule.decision_template.comment || '');
                    setCompensation(currentRule.decision_template.compensation || '');
                    setSelectedReason(null);
                    setReason('');
                }
            }

        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Fetch aborted');
                return;
            }
            console.error('Error fetching decisions:', error);
        } finally {
            if (!abortController.signal.aborted) {
                setLoading(false);
            }
        }
    };

    // Обновляем данные при изменении mpId
    useEffect(() => {
        const abortController = new AbortController();
        fetchDecisions(abortController);

        return () => {
            abortController.abort();
        };
    }, [mpId]);

    // Обновляем состояния при изменении currentRule
    useEffect(() => {
        if (currentRule?.decision_template) {
            setComment(currentRule.decision_template.comment || '');
            setCompensation(currentRule.decision_template.compensation || '');
            
            if (currentRule.decision_template.reason) {
                setReason(currentRule.decision_template.reason);
                setSelectedReason({
                    label: currentRule.decision_template.reason,
                    value: currentRule.decision_template.reason
                });
            } else {
                setReason('');
                setSelectedReason(null);
            }
        }
    }, [currentRule]);

    // Сброс дополнительных полей при смене решения
    useEffect(() => {
        if (!selectedDecisionData) {
            setReason('');
            setSelectedReason(null);
            setCompensation('');
            setComment('');
        }
    }, [selectedDecisionData]);

    // Обработчик выбора решения
    const handleDecisionSelect = (selected) => {
        setState(selected);
        const fullDecision = decisions.find(d => d.value === selected?.value);
        setSelectedDecisionData(fullDecision || null);

        // Сбрасываем все поля
        setReason('');
        setSelectedReason(null);
        setCompensation('');
        setComment('');

        // Если есть колбэк, передаем данные
        if (onDecisionChange) {
            onDecisionChange({
                decision: selected,
                fullData: fullDecision,
                comment: '',
                reason: '',
                selectedReason: null,
                compensation: ''
            });
        }
    };

    // Обработчик изменения комментария
    const handleCommentChange = (value) => {
        setComment(value);
        if (onDecisionChange) {
            onDecisionChange({
                decision: state,
                fullData: selectedDecisionData,
                comment: value,
                reason: reason,
                selectedReason: selectedReason,
                compensation: compensation
            });
        }
    };

    // Обработчик выбора причины
    const handleReasonSelect = (selected) => {
        setSelectedReason(selected);
        setReason(selected?.value || '');
        if (onDecisionChange) {
            onDecisionChange({
                decision: state,
                fullData: selectedDecisionData,
                comment: comment,
                reason: selected?.value || '',
                selectedReason: selected,
                compensation: compensation
            });
        }
    };

    // Обработчик изменения компенсации
    const handleCompensationChange = (value) => {
        setCompensation(value);
        if (onDecisionChange) {
            onDecisionChange({
                decision: state,
                fullData: selectedDecisionData,
                comment: comment,
                reason: reason,
                selectedReason: selectedReason,
                compensation: value
            });
        }
    };

    // Форматирование причин для DropDownSelector
    const formatReasons = (reasons) => {
        if (!reasons) return [];
        return reasons.map(reason => ({
            ...reason,
            label: reason.label,
            value: reason.value
        }));
    };

    // Проверка, нужно ли показывать дополнительные поля
    const hasAdditionalFields = () => {
        if (!selectedDecisionData) return false;
        return (
            selectedDecisionData.comment_params ||
            selectedDecisionData.reason_params?.available_reasons ||
            selectedDecisionData.compensation_params
        );
    };

    // Получение значения комментария из params
    const getCommentValue = () => {
        if (!selectedDecisionData?.comment_params) return comment;
        return comment;
    };

    // Получение значения компенсации из params
    const getCompensationValue = () => {
        if (!selectedDecisionData?.compensation_params) return compensation;
        return compensation;
    };

    return (
        <div className="decision-selector">
            <DropDownSelector
                required={required}
                state={state}
                options_prop={decisions}
                total={decisions?.length}
                setState={handleDecisionSelect}
                placeholder={placeholder}
                loading={loading}
                disabled={!decisions?.length}
                maxWidth='100%'
            />

            {/* Дополнительная информация и поля */}
            {selectedDecisionData && hasAdditionalFields() && (
                <div className="decision-selector__fields">
                    {/* Поле для комментария */}
                    {selectedDecisionData.comment_params && (
                        <TextareaDynamicPlaceholder
                            onChange={handleCommentChange}
                            placeholder={selectedDecisionData.comment_params.description || 'Введите комментарий'}
                            changeValue={getCommentValue()}
                            size='s'
                            tooltipStatic={selectedDecisionData.comment_params.description}
                            required={selectedDecisionData.comment_params.is_required}
                            maxWidth={'100%'}
                        />
                    )}

                    {/* Выбор причины */}
                    {selectedDecisionData.reason_params?.available_reasons && (
                        <DropDownSelector
                            required={selectedDecisionData.reason_params.is_required}
                            state={selectedReason}
                            options_prop={formatReasons(selectedDecisionData.reason_params.available_reasons)}
                            total={selectedDecisionData.reason_params.available_reasons?.length}
                            setState={handleReasonSelect}
                            placeholder="Выберите причину"
                        />
                    )}

                    {/* Поле для компенсации */}
                    {selectedDecisionData.compensation_params && (
                        <TextareaDynamicPlaceholder
                            onChange={handleCompensationChange}
                            placeholder={selectedDecisionData.compensation_params.description || 'Введите сумму компенсации'}
                            changeValue={getCompensationValue()}
                            size='s'
                            required={selectedDecisionData.compensation_params.is_required}
                            tooltipStatic={selectedDecisionData.compensation_params.description}
                            maxWidth={'100%'}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export { DecisionSelector };